// ============================================================================
// THE CYCLE — Hot/Cold Streaks Dashboard
// ============================================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, Typography, Grid, Avatar, Chip, Skeleton,
  useTheme, alpha, ToggleButton, ToggleButtonGroup, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  useMediaQuery, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Whatshot, AcUnit, TrendingUp, TrendingDown, TrendingFlat,
  FilterList,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { statsApi } from '../services/apiService';
import { getTeamLogoUrl } from '../utils/teamLogos';
import { useYear } from '../context/YearContext';
import PageYearSelector from '../components/common/PageYearSelector';
import { makePlayerSlug } from '../utils/playerSlug';

/* ── stat options per category ───────────────────────────────── */
const BATTING_STATS = [
  { value: 'ops',  label: 'OPS',  fmt: v => v?.toFixed(3) || '—' },
  { value: 'avg',  label: 'AVG',  fmt: v => v?.toFixed(3)?.replace(/^0/, '') || '—' },
  { value: 'slg',  label: 'SLG',  fmt: v => v?.toFixed(3) || '—' },
  { value: 'obp',  label: 'OBP',  fmt: v => v?.toFixed(3) || '—' },
  { value: 'woba', label: 'wOBA', fmt: v => v?.toFixed(3) || '—' },
];
const PITCHING_STATS = [
  { value: 'era',   label: 'ERA',  fmt: v => v?.toFixed(2) || '—' },
  { value: 'whip',  label: 'WHIP', fmt: v => v?.toFixed(2) || '—' },
  { value: 'strikeoutsPer9', label: 'K/9', fmt: v => v?.toFixed(1) || '—' },
  { value: 'fip',   label: 'FIP',  fmt: v => v?.toFixed(2) || '—' },
];

/* ── sensitivity presets ─────────────────────────────────────── */
const SENSITIVITY = [
  { value: 'strict', label: 'Strict', minGames: 15, minAB: 40, minIP: 15 },
  { value: 'normal', label: 'Normal', minGames: 5, minAB: 15, minIP: 5 },
  { value: 'loose',  label: 'Loose',  minGames: 2, minAB: 5, minIP: 2 },
];

/* ── MLB teams ───────────────────────────────────────────────── */
const MLB_TEAMS = [
  'ARI','ATL','BAL','BOS','CHC','CHW','CIN','CLE','COL','DET',
  'HOU','KC','LAA','LAD','MIA','MIL','MIN','NYM','NYY','OAK',
  'PHI','PIT','SD','SEA','SF','STL','TB','TEX','TOR','WSH',
];

/* ── position groups ─────────────────────────────────────────── */
const BATTER_POSITIONS = ['C','1B','2B','3B','SS','OF','DH'];
const PITCHER_POSITIONS = ['SP','RP'];

/* ── normalize positions to groups ─────────────────────────────── */
const normPos = (pos) => {
  if (!pos) return '';
  const p = pos.toUpperCase();
  if (['LF','CF','RF','OF'].includes(p)) return 'OF';
  if (p === 'P') return 'SP'; // fallback
  return p;
};

/* ── shared table-header style ─────────────────────────────────── */
const TH = { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 };

const getRankColor = (rank) => {
  if (rank === 1) return '#FFD700';
  if (rank === 2) return '#C0C0C0';
  if (rank === 3) return '#CD7F32';
  return 'transparent';
};

/* pitching stats where LOWER is better */
const LOWER_IS_BETTER = new Set(['era', 'whip', 'fip']);

const HotColdStreaks = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { year } = useYear();

  const [category, setCategory] = useState('batting');
  const [stat, setStat] = useState('ops');
  const [sensitivity, setSensitivity] = useState('normal');
  const [dataMode, setDataMode] = useState('season'); // 'season' | 'rolling'
  const [teamFilter, setTeamFilter] = useState('');
  const [posFilter, setPosFilter] = useState('');
  const [allPlayers, setAllPlayers] = useState([]);
  const [hotPlayers, setHotPlayers] = useState([]);
  const [coldPlayers, setColdPlayers] = useState([]);
  const [leagueAvg, setLeagueAvg] = useState(0);
  const [loading, setLoading] = useState(true);

  /* Keep stat in sync with category */
  useEffect(() => {
    setStat(category === 'batting' ? 'ops' : 'era');
  }, [category]);

  const statOptions = category === 'batting' ? BATTING_STATS : PITCHING_STATS;
  const currentStatCfg = useMemo(() => statOptions.find(s => s.value === stat) || statOptions[0], [stat, statOptions]);
  const sens = useMemo(() => SENSITIVITY.find(s => s.value === sensitivity) || SENSITIVITY[1], [sensitivity]);

  /* ── data ─────────────────────────────────────────────────────── */
  const loadStreaks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await statsApi.getLeaders({
        category, stat, limit: 300, year,
        minGames: sens.minGames, minAtBats: sens.minAB, minInnings: sens.minIP,
        mode: dataMode,
      });

      const players = (res?.leaders || []).map(e => {
        const name = e.player?.name || '';
        return {
          id: `${e.player?.team}-${name}`,
          name,
          team: e.player?.team || '',
          position: normPos(e.player?.position || ''),
          mlbId: e.player?.mlbId || null,
          games: e.games || 0,
          stats: e.fullStats || {},
          value: parseFloat(e.value || 0),
        };
      });

      // Calculate league average for context
      const values = players.map(p => p.value).filter(v => !isNaN(v) && v > 0);
      const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
      setLeagueAvg(avg);

      const invertDiff = LOWER_IS_BETTER.has(stat);
      const enriched = players.map(p => ({
        ...p,
        seasonVal: p.value,
        leagueAvg: avg,
        diff: invertDiff ? avg - p.value : p.value - avg,
      }));

      setAllPlayers(enriched);
    } catch (err) { console.error('Failed to load streaks:', err); }
    finally { setLoading(false); }
  }, [category, stat, year, sens, dataMode]);

  useEffect(() => { loadStreaks(); }, [loadStreaks]);

  /* ── apply team/position filters client-side ─────────────────── */
  useEffect(() => {
    let filtered = allPlayers;
    if (teamFilter) filtered = filtered.filter(p => p.team === teamFilter);
    if (posFilter) filtered = filtered.filter(p => p.position === posFilter);

    const sorted = [...filtered].sort((a, b) => b.diff - a.diff);
    setHotPlayers(sorted.filter(p => p.diff > 0).slice(0, 20));
    setColdPlayers(sorted.filter(p => p.diff < 0).sort((a, b) => a.diff - b.diff).slice(0, 20));
  }, [allPlayers, teamFilter, posFilter]);

  /* ── helpers ──────────────────────────────────────────────────── */
  const fmtVal = (v) => currentStatCfg.fmt(v);
  const diffClr = (d) => Math.abs(d) < 0.02 ? theme.palette.text.secondary : d > 0 ? '#2ecc71' : '#e74c3c';
  const statLabel = currentStatCfg.label;

  const TrendIcon = ({ diff }) => {
    if (Math.abs(diff) < 0.02) return <TrendingFlat sx={{ fontSize: 14, color: 'text.secondary' }} />;
    return diff > 0
      ? <TrendingUp sx={{ fontSize: 14, color: '#2ecc71' }} />
      : <TrendingDown sx={{ fontSize: 14, color: '#e74c3c' }} />;
  };

  /* ── spotlight cards ─────────────────────────────────────────── */
  const SpotlightCard = ({ player, type }) => {
    if (!player) return null;
    const isHot = type === 'hot';
    const accent = isHot ? '#e74c3c' : '#3498db';
    const icon = isHot ? <Whatshot sx={{ fontSize: 18 }} /> : <AcUnit sx={{ fontSize: 18 }} />;
    return (
      <Card variant="outlined" sx={{
        borderRadius: 2, overflow: 'hidden',
        border: `1px solid ${alpha(accent, 0.2)}`,
      }}>
        <Box sx={{
          py: 0.6, px: 1.5, display: 'flex', alignItems: 'center', gap: 0.75,
          background: `linear-gradient(135deg, ${alpha(accent, 0.9)}, ${alpha(accent, 0.6)})`,
          color: 'white',
        }}>
          {icon}
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
            {isHot ? 'Hottest Player' : 'Coldest Player'}
          </Typography>
        </Box>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={getTeamLogoUrl(player.team)} sx={{ width: 36, height: 36 }}
            imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={800} noWrap sx={{ fontSize: '0.85rem' }}>
              {player.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {player.team} · {player.games} GP
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" fontWeight={900} fontFamily="monospace" sx={{
              fontSize: '1rem', color: accent,
            }}>
              {player.diff > 0 ? '+' : ''}{player.diff.toFixed(3)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {statLabel} Δ
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  /* ── table component ─────────────────────────────────────────── */
  const PlayerTable = ({ players, type }) => {
    const isHot = type === 'hot';
    const accent = isHot ? '#e74c3c' : '#3498db';
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}>
        {/* gradient header */}
        <Box sx={{
          py: 0.5, px: 1.5, display: 'flex', alignItems: 'center', gap: 0.75,
          background: `linear-gradient(135deg, ${alpha(accent, 0.9)}, ${alpha(accent, 0.65)})`,
          color: 'white',
        }}>
          {isHot
            ? <Whatshot sx={{ fontSize: 16 }} />
            : <AcUnit sx={{ fontSize: 16 }} />}
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
            {isHot ? '🔥 Heating Up' : '❄️ Cooling Down'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip label={players.length} size="small" sx={{
            height: 18, fontSize: '0.75rem', fontWeight: 700,
            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
          }} />
        </Box>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, width: 28 }}>#</TableCell>
                <TableCell sx={TH}>Player</TableCell>
                <TableCell align="center" sx={TH}>{statLabel}</TableCell>
                <TableCell align="center" sx={TH}>Lg Avg</TableCell>
                {!isMobile && <TableCell align="center" sx={TH}>GP</TableCell>}
                <TableCell align="center" sx={TH}>Δ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players.map((p, i) => {
                const rank = i + 1;
                return (
                  <TableRow
                    key={p.id || i}
                    component={motion.tr}
                    initial={{ opacity: 0, x: isHot ? -8 : 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.5) }}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha(accent, 0.04) },
                      ...(rank <= 3 && { bgcolor: alpha(getRankColor(rank), 0.06) }),
                    }}
                    onClick={() => {
                      navigate(`/players/profile/${makePlayerSlug(p.name, p.mlbId)}`);
                    }}
                  >
                    <TableCell>
                      {rank <= 3 ? (
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '50%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          bgcolor: getRankColor(rank), color: '#000',
                          fontWeight: 800, fontSize: 11,
                        }}>
                          {rank}
                        </Box>
                      ) : (
                        <Typography variant="caption" fontWeight={700} color="text.secondary">{rank}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Avatar src={getTeamLogoUrl(p.team)} sx={{ width: 20, height: 20 }}
                          imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.78rem' }}>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {p.team}{p.position ? ` · ${p.position}` : ''}
            </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontFamily="monospace" fontWeight={700} sx={{
                        fontSize: '0.78rem', color: isHot ? '#2ecc71' : '#e74c3c',
                      }}>
                        {fmtVal(p.seasonVal)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600} sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                        {fmtVal(p.leagueAvg)}
                      </Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell align="center">
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {p.games || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                        <TrendIcon diff={p.diff} />
                        <Typography variant="caption" fontFamily="monospace" fontWeight={700} sx={{
                          fontSize: '0.75rem', color: diffClr(p.diff),
                        }}>
                          {p.diff > 0 ? '+' : ''}{p.diff.toFixed(3)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {players.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 6} sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="caption" color="text.secondary">No data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Footer */}
        <Box sx={{ px: 1.5, py: 0.75, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {players.length} players · {year} {dataMode === 'rolling' ? '30-day rolling' : 'season'} {statLabel} vs League Avg
          </Typography>
          <Chip label={`${category === 'batting' ? 'Batting' : 'Pitching'} · ${sens.label}`} size="small" variant="outlined"
            sx={{ height: 18, fontSize: '0.75rem', fontWeight: 600 }} />
        </Box>
      </Card>
    );
  };

  /* ── skeleton ─────────────────────────────────────────────────── */
  const TableSkeleton = () => (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={32} />
      {[...Array(8)].map((_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.6 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width="40%" height={18} />
          <Box sx={{ flex: 1 }} />
          <Skeleton variant="text" width={45} height={18} />
          <Skeleton variant="text" width={45} height={18} />
          <Skeleton variant="text" width={50} height={18} />
        </Box>
      ))}
    </Card>
  );

  /* ── render ───────────────────────────────────────────────────── */
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Whatshot sx={{ fontSize: 24, color: '#e74c3c' }} />
            <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.15rem', sm: '1.4rem' } }}>
              Hot & Cold Streaks
            </Typography>
          </Box>
          <ToggleButtonGroup value={category} exclusive size="small"
            onChange={(_, v) => { if (v) setCategory(v); }}
          >
            <ToggleButton value="batting" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.5 }}>Batting</ToggleButton>
            <ToggleButton value="pitching" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.5 }}>Pitching</ToggleButton>
          </ToggleButtonGroup>
          <PageYearSelector />
        </Box>

        {/* Controls row: stat selector + sensitivity + data mode */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.75rem' }}>Stat:</Typography>
            <ToggleButtonGroup value={stat} exclusive size="small"
              onChange={(_, v) => { if (v) setStat(v); }}
            >
              {statOptions.map(s => (
                <ToggleButton key={s.value} value={s.value} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 36 }}>
                  {s.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.75rem' }}>Window:</Typography>
            <ToggleButtonGroup value={dataMode} exclusive size="small"
              onChange={(_, v) => { if (v) setDataMode(v); }}
            >
              <ToggleButton value="season" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.7rem', px: 1, py: 0.25 }}>Season</ToggleButton>
              <ToggleButton value="rolling" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.7rem', px: 1, py: 0.25 }}>30-Day</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip title="Filter sensitivity controls minimum game/AB/IP thresholds">
              <FilterList sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
            <ToggleButtonGroup value={sensitivity} exclusive size="small"
              onChange={(_, v) => { if (v) setSensitivity(v); }}
            >
              {SENSITIVITY.map(s => (
                <ToggleButton key={s.value} value={s.value} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.7rem', px: 1, py: 0.25 }}>
                  {s.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Filter row: team + position */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ fontSize: '0.78rem' }}>Team</InputLabel>
            <Select
              value={teamFilter}
              label="Team"
              onChange={e => setTeamFilter(e.target.value)}
              sx={{ fontSize: '0.78rem' }}
            >
              <MenuItem value=""><em>All teams</em></MenuItem>
              {MLB_TEAMS.map(t => <MenuItem key={t} value={t} sx={{ fontSize: '0.78rem' }}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.75rem' }}>Pos:</Typography>
            <Chip label="All" size="small" clickable onClick={() => setPosFilter('')}
              sx={{ height: 22, fontSize: '0.72rem', fontWeight: !posFilter ? 700 : 400, bgcolor: !posFilter ? 'primary.main' : undefined, color: !posFilter ? 'white' : undefined }} />
            {(category === 'batting' ? BATTER_POSITIONS : PITCHER_POSITIONS).map(p => (
              <Chip key={p} label={p} size="small" clickable onClick={() => setPosFilter(posFilter === p ? '' : p)}
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: posFilter === p ? 700 : 400, bgcolor: posFilter === p ? 'primary.main' : undefined, color: posFilter === p ? 'white' : undefined }} />
            ))}
          </Box>
          {(teamFilter || posFilter) && (
            <Chip label="Clear filters" size="small" variant="outlined" clickable
              onClick={() => { setTeamFilter(''); setPosFilter(''); }}
              sx={{ height: 22, fontSize: '0.72rem' }} />
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontSize: '0.75rem' }}>
          {year} {dataMode === 'rolling' ? '30-day rolling' : 'season'} {statLabel} — players outperforming or underperforming the league average.
          {' '}Sensitivity: min {sens.minGames} GP{category === 'batting' ? `, ${sens.minAB} AB` : `, ${sens.minIP} IP`}.
          {(teamFilter || posFilter) && ` Filtered: ${[teamFilter, posFilter].filter(Boolean).join(' · ')}.`}
        </Typography>

        {loading ? (
          <Box>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6}><Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} /></Grid>
              <Grid item xs={12} sm={6}><Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} /></Grid>
            </Grid>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}><TableSkeleton /></Grid>
              <Grid item xs={12} md={6}><TableSkeleton /></Grid>
            </Grid>
          </Box>
        ) : (
          <Box>
            {/* Spotlight Cards */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6}>
                <SpotlightCard player={hotPlayers[0]} type="hot" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SpotlightCard player={coldPlayers[0]} type="cold" />
              </Grid>
            </Grid>
            {/* Tables */}
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <PlayerTable players={hotPlayers} type="hot" />
              </Grid>
              <Grid item xs={12} md={6}>
                <PlayerTable players={coldPlayers} type="cold" />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default HotColdStreaks;
